import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import { faGripVertical } from "@fortawesome/pro-solid-svg-icons/faGripVertical";
import { faTrash } from "@fortawesome/pro-solid-svg-icons/faTrash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Col from "antd/es/col";
import Form from "antd/es/form";
import { FormInstance } from "antd/es/form/hooks/useForm";
import Row from "antd/es/row";
import Space from "antd/es/space";
import Input from "antd/lib/input/Input";
import cn from "classnames";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";
import { defineMessages, useIntl } from "react-intl";

import Color from "@mapmycustomers/shared/enum/Color";
import DealStageType from "@mapmycustomers/shared/enum/DealStageType";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button, NumberField, Popconfirm } from "@mapmycustomers/ui";

import { deleteStage } from "@app/store/deal/actions";
import layout from "@app/styles/layout";
import anyColorToHex from "@app/util/colors/anyColorToHex";
import useErroredFieldsScroller from "@app/util/hook/useErroredFieldsScroller";
import isClosedStage from "@app/util/isClosedStage";

import styles from "./DealStage.module.scss";
import StageColor from "./StageColor";

interface Props {
  deleteStage: typeof deleteStage.request;
  funnelId: Funnel["id"];
  onDrop: (stages: Stage[]) => void;
  stageForm: FormInstance<{ stages: Stage[] }>;
  stages: Stage[];
}

const messages = defineMessages({
  name: {
    id: "deals.stages.validation.name",
    defaultMessage: "Stages cannot have same names",
    description: "Label for stages cannot have same names",
  },
  rottingIntervalDays: {
    id: "deals.stages.rottingInterval.days",
    defaultMessage: "{value} Days",
    description: "Days label",
  },
  rottingIntervalNone: {
    id: "deals.stages.rottingInterval.none",
    defaultMessage: "None",
    description: "None label",
  },
});

const DealStage: React.FC<Props> = ({ deleteStage, funnelId, onDrop, stageForm, stages }) => {
  const intl = useIntl();
  const [stageListToMap, setStageListToMap] = useState<Stage[]>([]);
  const [dragging, , , setDragging] = useBoolean();

  useEffect(() => {
    setStageListToMap(stages.map((stage) => ({ ...stage, weight: stage.weight * 100 })));
  }, [stages]);

  useEffect(() => {
    stageForm.setFieldsValue({ stages: [...stageListToMap] });
  }, [stageListToMap, stageForm]);

  const onDragEnd = useCallback(
    ({ destination, draggableId }: DropResult) => {
      const stageListFinal: Stage[] = [];
      if (destination) {
        const index = stages.findIndex((stage) => stage.id === parseInt(draggableId));
        const dataPopped = stages.splice(index, 1)[0];
        if (dataPopped) {
          stages.splice(destination.index, 0, dataPopped);
        }
        stages.forEach((stage, index) => {
          const result = { ...stage, displayOrder: index };
          if (result.weight > 0 && result.weight <= 1) {
            result.weight = result.weight * 100;
          }
          stageListFinal.push(result);
        });
        onDrop(stageListFinal);
      }
      setStageListToMap(stageListFinal);
      setDragging();
    },
    [onDrop, stages, setDragging, setStageListToMap]
  );

  const [formFieldsRef, handleFinishFail] = useErroredFieldsScroller();

  return (
    <DragDropContext onBeforeDragStart={() => setDragging()} onDragEnd={onDragEnd}>
      <div ref={formFieldsRef}>
        <Form
          className={cn(styles.fullWidth, [dragging ? styles.stagesFormMargin : undefined])}
          form={stageForm}
          initialValues={{ stages: [...stageListToMap] }}
          name={`stages_${funnelId}`}
          onFinishFailed={handleFinishFail}
        >
          <Droppable droppableId="droppable">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                <Form.List name="stages">
                  {() => (
                    <>
                      {stageListToMap.map((stage, index) => (
                        <Space align="baseline" key={stage.id} style={{ display: "block" }}>
                          <Draggable draggableId={`${stage.id}`} index={index} key={stage.id}>
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <Row gutter={[layout.spacerS, layout.spacerS]}>
                                  <Col className={styles.stageIcons} span={1}>
                                    <FontAwesomeIcon icon={faGripVertical} />
                                  </Col>
                                  <Col span={12}>
                                    <Form.Item
                                      className={styles.fullWidth}
                                      name={[index, "name"]}
                                      rules={[
                                        () => ({
                                          validator(_, value) {
                                            const ifExistingStage = stages.find(
                                              (st) => st.name === value && st.id !== stage.id
                                            );
                                            if (!ifExistingStage) {
                                              return Promise.resolve();
                                            }
                                            return Promise.reject(
                                              new Error(intl.formatMessage(messages.name))
                                            );
                                          },
                                        }),
                                      ]}
                                    >
                                      <Input disabled={isClosedStage(stage)} />
                                    </Form.Item>
                                  </Col>
                                  <Col span={3}>
                                    <Form.Item
                                      className={styles.fullWidth}
                                      name={[index, "weight"]}
                                    >
                                      <NumberField
                                        disabled={isClosedStage(stage)}
                                        formatter={(value) =>
                                          isClosedStage(stage) ? `--` : `${value}%`
                                        }
                                        max={100}
                                        min={0}
                                        size="middle"
                                        step={5}
                                      />
                                    </Form.Item>
                                  </Col>
                                  <Col span={3}>
                                    <Form.Item
                                      className={styles.fullWidth}
                                      name={[index, "color"]}
                                      rules={[
                                        ({ getFieldsValue }) => ({
                                          validator(_, value) {
                                            const isColorAlreadyUsed = getFieldsValue().stages.find(
                                              ({ id, color }: Stage) =>
                                                anyColorToHex(color) === anyColorToHex(value) &&
                                                id !== stage.id &&
                                                stage.type === DealStageType.DEFAULT
                                            );
                                            if (!isColorAlreadyUsed) {
                                              return Promise.resolve();
                                            }
                                            return Promise.reject(new Error());
                                          },
                                        }),
                                      ]}
                                    >
                                      <StageColor
                                        stage={stage}
                                        value={stage.color ?? Color.BLACK}
                                      />
                                    </Form.Item>
                                  </Col>
                                  <Col span={4}>
                                    <Form.Item
                                      className={styles.fullWidth}
                                      name={[index, "rottingInterval"]}
                                    >
                                      <NumberField
                                        className={styles.numberFields}
                                        disabled={isClosedStage(stage)}
                                        formatter={(value) =>
                                          isClosedStage(stage)
                                            ? `--`
                                            : value
                                            ? intl.formatMessage(messages.rottingIntervalDays, {
                                                value,
                                              })
                                            : intl.formatMessage(messages.rottingIntervalNone)
                                        }
                                        max={999}
                                        min={0}
                                        parser={(value) => value?.replace(/\D/g, "") ?? 0}
                                        size="middle"
                                      />
                                    </Form.Item>
                                  </Col>
                                  <Col span={1}>
                                    <Popconfirm
                                      cancelText={intl.formatMessage({
                                        id: "deals.stages.deleteButton.no",
                                        defaultMessage: "No",
                                        description: "No button for Delete Stage Pop Confirm",
                                      })}
                                      description={intl.formatMessage({
                                        id: "deals.stages.deleteButton.title",
                                        defaultMessage: "This stage will be deleted from the Org",
                                        description: "Title for Delete stage Pop Confirm",
                                      })}
                                      disabled={isClosedStage(stage)}
                                      okText={intl.formatMessage({
                                        id: "deals.stages.deleteButton.yes",
                                        defaultMessage: "Yes",
                                        description: "Yes button for Delete Stage Pop Confirm",
                                      })}
                                      onConfirm={() => deleteStage(stage)}
                                      placement="left"
                                    >
                                      <Button
                                        className={styles.deleteIcon}
                                        disabled={isClosedStage(stage)}
                                        icon={<FontAwesomeIcon icon={faTrash} />}
                                        iconOnly
                                        type="link"
                                      />
                                    </Popconfirm>
                                  </Col>
                                </Row>
                              </div>
                            )}
                          </Draggable>
                        </Space>
                      ))}
                    </>
                  )}
                </Form.List>
              </div>
            )}
          </Droppable>
        </Form>
      </div>
    </DragDropContext>
  );
};

const mapDispatchToProps = {
  deleteStage: deleteStage.request,
};

export default connect(null, mapDispatchToProps)(DealStage);
